import type { editor, IDisposable } from "monaco-editor"
import type { Awareness } from "y-protocols/awareness"

export type PlainAwarenessCursor = {
  index: number
  line: number
  column: number
  file: string
}

/** Backend wire format: cursor.anchor / cursor.head with plain { type, index }. */
export type WireAwarenessCursor = {
  anchor: { type: string; index: number }
  head: { type: string; index: number }
}

/** Push caret into awareness using JSON-safe cursor (survives awareness wire). */
export function pushMonacoSelectionToAwareness(
  editor: editor.IStandaloneCodeEditor,
  model: editor.ITextModel,
  awareness: Awareness,
  filePath: string,
): void {
  if (editor.getModel() !== model || model.isDisposed()) return

  const sel = editor.getSelection()
  if (!sel) return

  let anchor = model.getOffsetAt(sel.getStartPosition())
  let head = model.getOffsetAt(sel.getEndPosition())
  // monaco.SelectionDirection.RTL === 2
  if (sel.getDirection() === 2) {
    const tmp = anchor
    anchor = head
    head = tmp
  }

  const wireCursor: WireAwarenessCursor = {
    anchor: { type: filePath, index: anchor },
    head: { type: filePath, index: head },
  }

  const prev = awareness.getLocalState() ?? {}
  const { selection: _selection, ...rest } = prev as Record<string, unknown>
  awareness.setLocalState({
    ...rest,
    cursor: wireCursor,
  })

  if (process.env.NODE_ENV === "development") {
    console.log("[Cursors] local selection pushed to awareness", wireCursor)
  }
}

/** Keep awareness cursor in sync with caret moves (not only selection-range changes). */
export function attachMonacoAwarenessSelectionSync(
  editor: editor.IStandaloneCodeEditor,
  model: editor.ITextModel,
  awareness: Awareness,
  filePath: string,
): IDisposable {
  const push = () => pushMonacoSelectionToAwareness(editor, model, awareness, filePath)

  push()

  const disposables = [
    editor.onDidChangeCursorSelection(push),
    editor.onDidChangeCursorPosition(push),
    editor.onDidFocusEditorText(push),
    model.onDidChangeContent(push),
  ]

  return {
    dispose: () => disposables.forEach((d) => d.dispose()),
  }
}
